import React, { useState, useCallback, ReactNode, useMemo, useRef } from 'react'
import theme from '../../../styles/theme'

import VersatileGrid from '../../ui_reusable/VersatileGrid'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { EasyIconButton } from '../../ui_reusable/styles/Button.styles'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import CreateDataset from './databook/CreateDataset'
import ShowDatasetDropdown from '../../comp_reusable/datasets/ShowDatasetDropdown'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { HeaderMedium600Style } from '../../../styles/Text.styles'

import { StatsContainer } from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { SmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import {
  HStackRadioComplex,
  ClientExternalLabel,
} from './styles/Databook.styles'
import CustomIcons from '../../ui_reusable/CustomIcons'
import EditDataset from './databook/EditDataset'
import { useAuth } from '../../../context/AuthContext'
import {
  StandardRadio,
  RadioProvider,
} from '../../ui_reusable/StandardRadioButtons'

import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import { useClientContext } from '../../../context/ClientContext'
import { useProjectContext } from '../../../context/ProjectContext'
import DeleteDataset from './databook/DeleteDataset'
import MarketIcon from '../../ui_reusable/MarketIcon'
import QualityTagWithTooltip from '../../ui_reusable/QualityTagWithTooltip'
import EditRegionDataset from './databook/region_dataset/EditRegionDataset'
import DeleteRegionDataset from './databook/region_dataset/DeleteRegionDataset'
import EditChannel from './databook/channel/EditChannel'
import DeleteChannel from './databook/channel/DeleteChannel'

// Types
import type { Dataset } from '../../../api/generated/models'
import type { MediaChannel } from '../../../api/generated/models'
import type { RegionDataset } from '../../../api/generated/models'
import UploadDataset from './databook/UploadDataset'
import { useQuery } from '@tanstack/react-query'

interface MediaChannelWithDatasets {
  id?: number
  name?: string
  datasets?: DatasetWithRegionalVariants[]
  owner_client?: { id?: number; name?: string } | null
}

interface DatasetWithRegionalVariants extends Dataset {
  regional_variants?: RegionDataset[]
}

interface ExtendedDataset extends DatasetWithRegionalVariants {
  _searchId: string
  _rowType: 'channel' | 'dataset' | 'segment'
  _level: number
}

interface ExtendedRegionDataset extends RegionDataset {
  _searchId: string
  _rowType: 'segment'
  _level: number
  _parentDataset?: Dataset
}

type DatabookRow = ExtendedDataset | ExtendedRegionDataset | (MediaChannelWithDatasets & {
  _searchId: string
  _rowType: 'channel'
  _level: number
  name: string
  source?: string
})

interface UnifiedDriversResponse {
  status?: string
  data?: {
    media_channels?: MediaChannelWithDatasets[]
    datasets_without_channel?: DatasetWithRegionalVariants[]
  }
  total?: number
  page?: number
  last_page?: number
  meta?: {
    good?: number
    check?: number
    fix?: number
  }
}

const PAGE_SIZE = 10
const EMPTY_CHANNELS: MediaChannelWithDatasets[] = []
const EMPTY_DATASETS: DatasetWithRegionalVariants[] = []

const DatabookPage: React.FC = () => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null)
  const [editRegionModalOpen, setEditRegionModalOpen] = useState<boolean>(false)
  const [deleteRegionModalOpen, setDeleteRegionModalOpen] =
    useState<boolean>(false)
  const [selectedRegionDataset, setSelectedRegionDataset] =
    useState<RegionDataset | null>(null)
  const [editChannelModalOpen, setEditChannelModalOpen] =
    useState<boolean>(false)
  const [deleteChannelModalOpen, setDeleteChannelModalOpen] =
    useState<boolean>(false)
  const [selectedChannel, setSelectedChannel] = useState<MediaChannel | null>(
    null,
  )
  const { isAdmin } = useAuth()
  const [scopeParam, setScopeParam] = useState<
    'contextual' | 'client' | null
  >(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    null,
  )
  const { selectedClient } = useClientContext()
  const { selectedProject } = useProjectContext()

  const listParams = useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(PAGE_SIZE))
    if (selectedClient?.id) params.set('client_id', String(selectedClient.id))
    if (selectedProject?.id) params.set('project_id', String(selectedProject.id))
    if (scopeParam) params.set('scope', scopeParam)
    if (search.trim()) params.set('search', search.trim())
    if (sort?.key) {
      params.set(
        'ordering',
        `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
      )
    }
    return params.toString()
  }, [
    page,
    selectedClient?.id,
    selectedProject?.id,
    scopeParam,
    search,
    sort,
  ])

  const { data: listResponse, isLoading } = useQuery({
    queryKey: ['/api/v2/datasets/drivers/', listParams],
    queryFn: async (): Promise<UnifiedDriversResponse> => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v2/datasets/drivers/?${listParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to load databook drivers')
      }
      return response.json()
    },
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  })

  const mediaChannels = listResponse?.data?.media_channels || EMPTY_CHANNELS
  const datasetsWithoutChannel =
    listResponse?.data?.datasets_without_channel || EMPTY_DATASETS
  const serverTotal = listResponse?.total ?? 0
  const serverLastPage = listResponse?.last_page
  const qualityGood = listResponse?.meta?.good ?? 0
  const qualityCheck = listResponse?.meta?.check ?? 0
  const qualityFix = listResponse?.meta?.fix ?? 0

  const processedData = useMemo(() => {
    const flatData: DatabookRow[] = []

    mediaChannels.forEach((channel) => {
      flatData.push({
        ...channel,
        name: channel.name || '',
        source: '',
        _searchId: `channel-${channel.id}`,
        _rowType: 'channel',
        _level: 0,
      })

      ;(channel.datasets || []).forEach((dataset) => {
        flatData.push({
          ...dataset,
          _searchId: `dataset-${dataset.id}`,
          _rowType: 'dataset',
          _level: 1,
        })

        ;(dataset.regional_variants || []).forEach((regionDataset) => {
          flatData.push({
            ...regionDataset,
            _searchId: `segment-${regionDataset.id}`,
            _rowType: 'segment',
            _level: 2,
            _parentDataset: dataset,
          } as ExtendedRegionDataset)
        })
      })
    })

    datasetsWithoutChannel.forEach((dataset) => {
      flatData.push({
        ...dataset,
        _searchId: `dataset-${dataset.id ?? 'orphan'}`,
        _rowType: 'dataset',
        _level: 0,
      })

      ;(dataset.regional_variants || []).forEach((regionDataset) => {
        flatData.push({
          ...regionDataset,
          _searchId: `segment-${regionDataset.id}`,
          _rowType: 'segment',
          _level: 1,
          _parentDataset: dataset,
        } as ExtendedRegionDataset)
      })
    })

    return flatData
  }, [mediaChannels, datasetsWithoutChannel])

  const handleExpand = (dataIdx: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(dataIdx)) {
        next.delete(dataIdx)
      } else {
        next.add(dataIdx)
      }
      return next
    })
  }

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term: string) => {
    const next = term ?? ''
    setSearch((prev) => (prev === next ? prev : next))
  }, [])

  const handleSortChange = useCallback(
    (nextSort: { key: string; direction: 'asc' | 'desc' } | null) => {
      setSort(nextSort)
      setPage(1)
    },
    [],
  )

  const handleScopeChange = useCallback((value: string) => {
    const next =
      value === 'contextual' || value === 'client' ? value : null
    setScopeParam((prev) => (prev === next ? prev : next))
  }, [])

  // Reset to page 1 only when a filter actually changes after first render.
  const seenFiltersRef = useRef(false)
  React.useEffect(() => {
    if (!seenFiltersRef.current) {
      seenFiltersRef.current = true
      return
    }
    setPage(1)
  }, [selectedClient?.id, selectedProject?.id, scopeParam, search])

  const handleEdit = (dataset: Dataset): void => {
    setSelectedDataset(dataset)
    setEditModalOpen(true)
  }

  const handleCloseEditModal = (): void => {
    setEditModalOpen(false)
    setSelectedDataset(null)
  }

  const handleDelete = (dataset: Dataset): void => {
    setSelectedDataset(dataset)
    setDeleteModalOpen(true)
  }

  const handleCloseDeleteModal = (): void => {
    setDeleteModalOpen(false)
    setSelectedDataset(null)
  }

  const handleEditRegionDataset = (regionDataset: RegionDataset): void => {
    setSelectedRegionDataset(regionDataset)
    setEditRegionModalOpen(true)
  }

  const handleCloseEditRegionModal = (): void => {
    setEditRegionModalOpen(false)
    setSelectedRegionDataset(null)
  }

  const handleDeleteRegionDataset = (regionDataset: RegionDataset): void => {
    setSelectedRegionDataset(regionDataset)
    setDeleteRegionModalOpen(true)
  }

  const handleCloseDeleteRegionModal = (): void => {
    setDeleteRegionModalOpen(false)
    setSelectedRegionDataset(null)
  }

  const handleEditChannel = (channel: MediaChannel): void => {
    setSelectedChannel(channel)
    setEditChannelModalOpen(true)
  }

  const handleCloseEditChannelModal = (): void => {
    setEditChannelModalOpen(false)
    setSelectedChannel(null)
  }

  const handleDeleteChannel = (channel: MediaChannel): void => {
    setSelectedChannel(channel)
    setDeleteChannelModalOpen(true)
  }

  const handleCloseDeleteChannelModal = (): void => {
    setDeleteChannelModalOpen(false)
    setSelectedChannel(null)
  }

  const headers = [
    {
      title: 'Dataset name',
      key: 'name',
      type: 'string' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'name',
      flexGrow: 2,
    },
    {
      title: 'Type',
      key: '_rowType',
      type: 'string' as const,
      isSortable: false,
      isSearchable: true,
    },
    {
      title: 'Source',
      key: 'source',
      type: 'string' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'source',
    },
    {
      title: 'Country',
      key: 'markets',
      type: 'string' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'markets',
      center: true,
      cellContentStyle: { overflow: 'visible' },
      getSortValue: (row: Dataset) =>
        row.markets?.map((m) => m.name).join(', ') || '',
    },
    {
      title: 'Granularity',
      key: 'interval',
      type: 'string' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'interval',
      center: true,
    },
    {
      title: 'Last updated',
      key: 'updated_at',
      type: 'datetime' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'updated_at',
    },
    {
      title: 'Observations',
      key: 'total_observations',
      type: 'number' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'total_observations',
      center: true,
    },
    {
      title: 'Measure',
      key: 'units',
      type: 'string' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'units',
      center: true,
    },
    {
      title: 'Quality',
      key: 'quality',
      type: 'string' as const,
      isSortable: true,
      isSearchable: true,
      serverKey: 'quality',
      center: true,
    },
    {
      title: `View${isAdmin ? '/Edit/Delete' : ''}`,
      key: 'actions',
      type: 'none' as any,
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  const gridHeader: ReactNode = (
    <StatsContainer>
      <div
        style={{
          width: '42%',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span css={HeaderMedium600Style}>Total: {serverTotal}</span>
        <div style={{ display: 'flex', justifyItems: 'center' }}>
          <SmallNormalTag>{qualityGood} good</SmallNormalTag>
          <SmallNormalTag>{qualityCheck} check</SmallNormalTag>
          <SmallNormalTag>{qualityFix} fix</SmallNormalTag>
        </div>
      </div>
      <div
        style={{
          width: '50%',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'space-between',
        }}
      >
        <RadioProvider
          initValue="all"
          group="driverType"
          onChange={handleScopeChange}
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <HStackRadioComplex style={{ paddingLeft: '5 rem' }}>
              <StandardRadio size={'m'} group="driverType" value="all" />
              <ClientExternalLabel>All</ClientExternalLabel>
            </HStackRadioComplex>
            <HStackRadioComplex style={{ paddingLeft: '5 rem' }}>
              <StandardRadio size={'m'} group="driverType" value="client" />
              <ClientExternalLabel>Internal</ClientExternalLabel>
            </HStackRadioComplex>
            <HStackRadioComplex>
              <StandardRadio size={'m'} group="driverType" value="contextual" />
              <ClientExternalLabel>External</ClientExternalLabel>
            </HStackRadioComplex>
          </div>
        </RadioProvider>
      </div>
    </StatsContainer>
  )

  const renderIntersectingRow = useCallback(
    (rowData: any, rowIdx: number, dataIdx: number): ReactNode | null => {
      if (
        (rowData._rowType !== 'dataset' && rowData._rowType !== 'segment') ||
        !expandedRows.has(dataIdx)
      ) {
        return null
      }

      return (
        <ShowDatasetDropdown
          dataset={rowData}
          datasetId={rowData.id!}
          units={rowData.units || rowData._parentDataset?.units}
          isSegment={rowData._rowType === 'segment'}
        />
      )
    },
    [expandedRows],
  )

  const renderRow = useCallback(
    (rowData: any, rowIdx: number, dataIdx: number): ReactNode[] => {
      const renderNestedName = (
        name: string,
        level: number,
        isBold: boolean,
      ): ReactNode => {
        const CellComponent = isBold ? GridCellBoldText : GridCellNormalText
        return (
          <CellComponent
            style={{ paddingLeft: `${level * 0.5}rem`, width: '100%' }}
          >
            {level > 0 && (
              <CustomIcons.CornerDownRight size="xs" clickDisabled />
            )}
            <span style={{ marginLeft: level > 0 ? '0.5rem' : undefined }}>
              {name}
            </span>
          </CellComponent>
        )
      }

      if (rowData._rowType === 'channel') {
        return [
          <GridCellBoldText
            style={{
              padding: '0.5rem',
              width: '100%',
            }}
          >
            {rowData.name}
          </GridCellBoldText>,
          <GridCellNormalText>Channel</GridCellNormalText>,
          ...Array(headers.length - 3).fill(<></>),
          <div style={{ display: 'flex', gap: '0px', justifyContent: 'end' }}>
            <EasyIconButton style={{ cursor: 'default' }}></EasyIconButton>
            {isAdmin && (
              <EasyIconButton
                onClick={() => handleEditChannel(rowData)}
                data-testid={`edit-channel-${rowData.id}`}
              >
                <CustomIcons.PenIcon
                  color={theme.colors.veryFadedText}
                  size="xs"
                />
              </EasyIconButton>
            )}
            {isAdmin && (
              <EasyIconButton
                onClick={() => handleDeleteChannel(rowData)}
                data-testid={`delete-channel-${rowData.id}`}
              >
                <CustomIcons.TrashIcon
                  color={theme.colors.veryFadedText}
                  size="xs"
                />
              </EasyIconButton>
            )}
          </div>,
        ]
      }

      if (rowData._rowType === 'segment') {
        const regionDataset = rowData as ExtendedRegionDataset
        const displayName = regionDataset.region?.name || 'Unknown Segment'

        return [
          renderNestedName(displayName, rowData._level, false),
          <GridCellNormalText>Segment</GridCellNormalText>,
          <GridCellNormalText>
            {regionDataset._parentDataset?.source || ''}
          </GridCellNormalText>,
          <div style={{ display: 'flex', justifyContent: 'center' }}></div>,
          <GridCellNormalText>{regionDataset.interval}</GridCellNormalText>,
          <GridCellSmallFadedText>
            {regionDataset.updated_at
              ? new Date(regionDataset.updated_at).toISOString().slice(0, 10)
              : ''}
          </GridCellSmallFadedText>,
          <GridCellNormalText>
            {regionDataset.total_observations}
          </GridCellNormalText>,
          <GridCellNormalText>
            {regionDataset._parentDataset?.units || ''}
          </GridCellNormalText>,
          <GridCellNormalText>
            {regionDataset.quality ? (
              <QualityTagWithTooltip
                quality={regionDataset.quality as 'good' | 'check' | 'fix'}
                statusText={regionDataset._parentDataset?.status_text}
              />
            ) : (
              ''
            )}
          </GridCellNormalText>,
          <div
            style={{ display: 'flex', gap: '0px', justifyContent: 'center' }}
          >
            <EasyIconButton
              onClick={() => handleExpand(dataIdx)}
              data-testid={`expand-${displayName}`}
            >
              {expandedRows.has(dataIdx) ? (
                <CustomIcons.faEyeSlash
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              ) : (
                <CustomIcons.faEye
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              )}
            </EasyIconButton>
            {isAdmin && (
              <EasyIconButton
                onClick={() => handleEditRegionDataset(regionDataset)}
                data-testid={`edit-region-${regionDataset.id}`}
              >
                <CustomIcons.PenIcon
                  color={theme.colors.veryFadedText}
                  size="xs"
                />
              </EasyIconButton>
            )}
            {isAdmin && (
              <EasyIconButton
                onClick={() => handleDeleteRegionDataset(regionDataset)}
                data-testid={`delete-region-${regionDataset.id}`}
              >
                <CustomIcons.TrashIcon
                  color={theme.colors.veryFadedText}
                  size="xs"
                />
              </EasyIconButton>
            )}
          </div>,
        ]
      }

      if (rowData._rowType === 'dataset') {
        const isTopLevel = rowData._level === 0

        return [
          renderNestedName(rowData.name, rowData._level, isTopLevel),
          <GridCellNormalText>Dataset</GridCellNormalText>,
          <GridCellNormalText>{rowData.source}</GridCellNormalText>,
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <MarketIcon
              isClient={rowData.client !== null}
              markets={rowData.markets as any}
            />
          </div>,
          <GridCellNormalText>{rowData.interval}</GridCellNormalText>,
          <GridCellSmallFadedText>
            {rowData.updated_at
              ? new Date(rowData.updated_at).toISOString().slice(0, 10)
              : ''}
          </GridCellSmallFadedText>,
          <GridCellNormalText>{rowData.total_observations}</GridCellNormalText>,
          <GridCellNormalText>{rowData.units}</GridCellNormalText>,
          <GridCellNormalText>
            {rowData.quality ? (
              <QualityTagWithTooltip
                quality={rowData.quality as 'good' | 'check' | 'fix'}
                statusText={rowData.status_text}
              />
            ) : (
              ''
            )}
          </GridCellNormalText>,
          <div
            style={{ display: 'flex', gap: '0px', justifyContent: 'center' }}
          >
            <EasyIconButton
              onClick={() => handleExpand(dataIdx)}
              data-testid={`expand-${rowData.name}`}
            >
              {expandedRows.has(dataIdx) ? (
                <CustomIcons.faEyeSlash
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              ) : (
                <CustomIcons.faEye
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              )}
            </EasyIconButton>
            {isAdmin && rowData.id && (
              <EasyIconButton
                onClick={() => handleEdit(rowData)}
                data-testid={`edit-${rowData.name}`}
              >
                <CustomIcons.PenIcon
                  color={theme.colors.veryFadedText}
                  size="xs"
                />
              </EasyIconButton>
            )}
            {isAdmin && rowData.id && (
              <EasyIconButton
                onClick={() => handleDelete(rowData)}
                data-testid={`delete-${rowData.name}`}
              >
                <CustomIcons.TrashIcon
                  color={theme.colors.veryFadedText}
                  size="xs"
                />
              </EasyIconButton>
            )}
          </div>,
        ]
      }

      return []
    },
    [expandedRows, isAdmin, headers.length],
  )

  return (
    <>
      <HeaderContainer>
        <SectionTitle>Manage Databook</SectionTitle>
        <ClientProjectPicker style={{}} />
      </HeaderContainer>

      <SimpleAccordion id={'datasetPageAccordion'} defaultActiveKey={['99']}>
        <SimpleAccordion.Panel eventKey="99" noCollapse>
          {isAdmin && <UploadDataset />}
          {isAdmin && <CreateDataset />}

          <VersatileGrid
            headers={headers}
            data={processedData}
            searchPlaceholder="Find dataset"
            renderRow={renderRow}
            renderIntersectingRow={renderIntersectingRow}
            headerInfo={gridHeader}
            maxDisplayedItems={PAGE_SIZE}
            loading={isLoading}
            loadingMessage={'Loading datasets...'}
            serverSide
            serverTotal={serverTotal}
            serverLastPage={serverLastPage}
            serverPage={page}
            initialSearchTerm={search}
            onPageChange={handlePageChange}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
          />
        </SimpleAccordion.Panel>
      </SimpleAccordion>

      {selectedDataset && (
        <EditDataset
          dataset={selectedDataset}
          open={editModalOpen}
          onClose={handleCloseEditModal}
        />
      )}
      {selectedDataset && (
        <DeleteDataset
          dataset={selectedDataset}
          onSuccess={() => {}}
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
        />
      )}
      {selectedRegionDataset && (
        <EditRegionDataset
          regionDataset={selectedRegionDataset}
          open={editRegionModalOpen}
          onClose={handleCloseEditRegionModal}
        />
      )}
      {selectedRegionDataset && (
        <DeleteRegionDataset
          regionDataset={selectedRegionDataset}
          open={deleteRegionModalOpen}
          onClose={handleCloseDeleteRegionModal}
          onSuccess={() => {}}
        />
      )}
      {selectedChannel && (
        <EditChannel
          channel={selectedChannel}
          open={editChannelModalOpen}
          onClose={handleCloseEditChannelModal}
        />
      )}
      {selectedChannel && (
        <DeleteChannel
          channel={selectedChannel}
          open={deleteChannelModalOpen}
          onClose={handleCloseDeleteChannelModal}
          onSuccess={() => {}}
        />
      )}
    </>
  )
}

export default DatabookPage
